import { ChevronUp, ChevronDown, Trash2, GripVertical, Plus, X as XIcon, ChevronRight } from 'lucide-react';
import type { LinkItem, LinkKind, VCardData, VCardSocial, VCardSocialKind } from '../types';

const VCARD_SOCIAL_OPTIONS: Array<{ value: VCardSocialKind; label: string; placeholder: string; showAt: boolean }> = [
  { value: 'x',         label: 'X',           placeholder: 'yourhandle',      showAt: true },
  { value: 'linkedin',  label: 'LinkedIn',    placeholder: 'your-profile',    showAt: false },
  { value: 'github',    label: 'GitHub',      placeholder: 'yourhandle',      showAt: true },
  { value: 'youtube',   label: 'YouTube',     placeholder: 'yourchannel',     showAt: true },
  { value: 'instagram', label: 'Instagram',   placeholder: 'yourhandle',      showAt: true },
  { value: 'bluesky',   label: 'Bluesky',     placeholder: 'you.bsky.social', showAt: true },
  { value: 'threads',   label: 'Threads',     placeholder: 'yourhandle',      showAt: true },
  { value: 'devto',     label: 'Dev.to',      placeholder: 'yourhandle',      showAt: false },
  { value: 'medium',    label: 'Medium',      placeholder: 'yourhandle',      showAt: true },
  { value: 'twitch',    label: 'Twitch',      placeholder: 'yourhandle',      showAt: false },
];

interface Props {
  link: LinkItem;
  index: number;
  total: number;
  expanded?: boolean;
  onToggle: () => void;
  onChange: (patch: Partial<LinkItem>) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}

export const kindOptions: Array<{ value: LinkKind; label: string }> = [
  { value: 'x', label: 'X' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'github', label: 'GitHub' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'bluesky', label: 'Bluesky' },
  { value: 'threads', label: 'Threads' },
  { value: 'devto', label: 'Dev.to' },
  { value: 'medium', label: 'Medium' },
  { value: 'twitch', label: 'Twitch' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'spotify', label: 'Spotify' },
  { value: 'discord', label: 'Discord' },
  { value: 'website', label: 'Website' },
  { value: 'blog', label: 'Blog' },
  { value: 'vcard', label: 'Save Contact (vCard)' },
  { value: 'custom', label: 'Custom' },
];

interface HandleConfig {
  base: string;
  showAt: boolean;
  placeholder: string;
  fieldLabel: string;
}

const HANDLE_CONFIG: Record<string, HandleConfig> = {
  x:         { base: 'https://x.com/',              showAt: true,  placeholder: 'yourhandle',        fieldLabel: 'Handle' },
  github:    { base: 'https://github.com/',          showAt: true,  placeholder: 'yourhandle',        fieldLabel: 'Handle' },
  linkedin:  { base: 'https://linkedin.com/in/',     showAt: false, placeholder: 'your-profile',      fieldLabel: 'Profile slug' },
  youtube:   { base: 'https://youtube.com/@',        showAt: true,  placeholder: 'yourchannel',       fieldLabel: 'Handle' },
  instagram: { base: 'https://instagram.com/',       showAt: true,  placeholder: 'yourhandle',        fieldLabel: 'Handle' },
  bluesky:   { base: 'https://bsky.app/profile/',    showAt: true,  placeholder: 'you.bsky.social',   fieldLabel: 'Handle' },
  threads:   { base: 'https://threads.net/@',        showAt: true,  placeholder: 'yourhandle',        fieldLabel: 'Handle' },
  devto:     { base: 'https://dev.to/',              showAt: false, placeholder: 'yourhandle',        fieldLabel: 'Username' },
  medium:    { base: 'https://medium.com/@',         showAt: true,  placeholder: 'yourhandle',        fieldLabel: 'Handle' },
  twitch:    { base: 'https://twitch.tv/',           showAt: false, placeholder: 'yourhandle',        fieldLabel: 'Username' },
  tiktok:    { base: 'https://tiktok.com/@',         showAt: true,  placeholder: 'yourhandle',        fieldLabel: 'Handle' },
  spotify:   { base: 'https://open.spotify.com/user/', showAt: false, placeholder: 'your-user-id',   fieldLabel: 'Username or profile URL' },
};

function urlToHandle(kind: string, url: string): string {
  const cfg = HANDLE_CONFIG[kind];
  if (!cfg || !url) return url ?? '';
  if (url.startsWith(cfg.base)) return url.slice(cfg.base.length);
  return url;
}

function handleToUrl(kind: string, raw: string): string {
  const cfg = HANDLE_CONFIG[kind];
  if (!cfg) return raw;
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
  const handle = raw.replace(/^@/, '').trim();
  if (!handle) return '';
  return cfg.base + handle;
}

/** Compact summary text shown when collapsed */
function summaryText(link: LinkItem): string {
  if (link.kind === 'vcard') {
    const name = [link.vcard?.firstName, link.vcard?.lastName].filter(Boolean).join(' ');
    return name || 'Untitled contact';
  }
  const handleCfg = HANDLE_CONFIG[link.kind];
  if (handleCfg && link.url) {
    const handle = urlToHandle(link.kind, link.url);
    return handleCfg.showAt ? `@${handle}` : handle;
  }
  return link.url || 'No URL set';
}

export function LinkRow({ link, index, total, expanded = false, onToggle, onChange, onMoveUp, onMoveDown, onRemove }: Props) {
  const isFirst = index === 0;
  const isLast = index === total - 1;
  const handleCfg = HANDLE_CONFIG[link.kind];
  const isHandleKind = !!handleCfg;
  const isVcard = link.kind === 'vcard';

  const handleValue = isHandleKind ? urlToHandle(link.kind, link.url) : link.url;
  const resolvedUrl = isHandleKind ? handleToUrl(link.kind, handleValue) : link.url;
  const showPreview = isHandleKind && !!resolvedUrl && resolvedUrl !== handleValue;

  const updateVcard = (patch: Partial<VCardData>) => {
    const current: VCardData = link.vcard ?? { firstName: '' };
    onChange({ vcard: { ...current, ...patch } });
  };

  const kindLabel = kindOptions.find((o) => o.value === link.kind)?.label ?? link.kind;

  return (
    <div className="bg-muted border border-border rounded-lg overflow-hidden">
      {/* Collapsed header — always visible */}
      <div
        className="flex items-center gap-2 px-3 py-2.5 cursor-pointer select-none hover:bg-surface/50 transition-colors"
        onClick={onToggle}
        role="button"
        aria-expanded={expanded}
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
      >
        <ChevronRight className={`w-4 h-4 text-text-muted transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`} />
        <GripVertical className="w-4 h-4 text-text-muted shrink-0" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground truncate">
              {link.label || kindLabel}
            </span>
            <span className="text-xs text-text-muted bg-surface px-1.5 py-0.5 rounded shrink-0">
              {kindLabel}
            </span>
          </div>
          {!expanded && (
            <p className="text-xs text-text-muted truncate mt-0.5">
              {summaryText(link)}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={onMoveUp}
            disabled={isFirst}
            className="p-1 rounded text-text-muted hover:text-foreground hover:bg-surface transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            aria-label="Move link up"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={isLast}
            className="p-1 rounded text-text-muted hover:text-foreground hover:bg-surface transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            aria-label="Move link down"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="p-1 rounded text-text-muted hover:text-destructive hover:bg-surface transition-colors cursor-pointer"
            aria-label="Remove link"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div className="px-3 pb-3 pt-1 space-y-3 border-t border-border">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label
                htmlFor={`link-kind-${link.linkKey}`}
                className="block text-xs font-medium text-text-muted"
              >
                Type
              </label>
              <select
                id={`link-kind-${link.linkKey}`}
                value={link.kind}
                onChange={(e) => {
                  const newKind = e.target.value as LinkKind;
                  const newCfg = HANDLE_CONFIG[newKind];
                  const currentHandle = HANDLE_CONFIG[link.kind]
                    ? urlToHandle(link.kind, link.url)
                    : link.url;
                  const oldKindLabel = kindOptions.find((o) => o.value === link.kind)?.label ?? '';
                  const newKindLabel = kindOptions.find((o) => o.value === newKind)?.label ?? '';
                  const patch: Partial<LinkItem> = { kind: newKind };
                  if (!link.label || link.label === oldKindLabel) patch.label = newKindLabel;
                  if (newKind === 'vcard') {
                    patch.url = '';
                    patch.vcard = link.vcard ?? { firstName: '' };
                  } else {
                    patch.url = newCfg ? handleToUrl(newKind, currentHandle) : link.url;
                    patch.vcard = undefined;
                  }
                  onChange(patch);
                }}
                className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent transition-colors duration-150"
              >
                {kindOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label
                htmlFor={`link-label-${link.linkKey}`}
                className="block text-xs font-medium text-text-muted"
              >
                Label
              </label>
              <input
                id={`link-label-${link.linkKey}`}
                type="text"
                value={link.label}
                onChange={(e) => onChange({ label: e.target.value })}
                placeholder={kindOptions.find((o) => o.value === link.kind)?.label ?? 'Link'}
                className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent transition-colors duration-150"
              />
            </div>
          </div>

          {isVcard ? (
            <VcardFields linkKey={link.linkKey} vcard={link.vcard} onChange={updateVcard} />
          ) : (
            <div className="space-y-1">
              <label
                htmlFor={`link-url-${link.linkKey}`}
                className="block text-xs font-medium text-text-muted"
              >
                {isHandleKind ? handleCfg.fieldLabel : 'URL'}
              </label>
              {isHandleKind ? (
                <div className="space-y-1">
                  <div className="relative flex items-center">
                    {handleCfg.showAt && (
                      <span className="absolute left-3 text-sm text-text-muted select-none pointer-events-none">
                        @
                      </span>
                    )}
                    <input
                      id={`link-url-${link.linkKey}`}
                      type="text"
                      value={handleValue}
                      onChange={(e) => onChange({ url: handleToUrl(link.kind, e.target.value) })}
                      placeholder={handleCfg.placeholder}
                      className={`w-full bg-surface border border-border rounded-lg py-2 text-sm text-foreground placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent transition-colors duration-150 ${handleCfg.showAt ? 'pl-7 pr-3' : 'px-3'}`}
                    />
                  </div>
                  {showPreview && (
                    <p className="text-xs text-text-muted truncate" title={resolvedUrl}>
                      {resolvedUrl}
                    </p>
                  )}
                </div>
              ) : (
                <input
                  id={`link-url-${link.linkKey}`}
                  type="url"
                  value={link.url}
                  onChange={(e) => onChange({ url: e.target.value })}
                  placeholder="https://example.com"
                  className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent transition-colors duration-150"
                />
              )}
            </div>
          )}

          {link.kind === 'custom' && (
            <div className="space-y-1">
              <label
                htmlFor={`link-icon-${link.linkKey}`}
                className="block text-xs font-medium text-text-muted"
              >
                Icon <span className="text-text-muted font-normal">(lucide icon name)</span>
              </label>
              <input
                id={`link-icon-${link.linkKey}`}
                type="text"
                value={link.icon ?? ''}
                onChange={(e) => onChange({ icon: e.target.value })}
                placeholder="e.g. Rocket"
                className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent transition-colors duration-150"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface VcardFieldsProps {
  linkKey: string;
  vcard?: VCardData;
  onChange: (patch: Partial<VCardData>) => void;
}

function VcardFields({ linkKey, vcard, onChange }: VcardFieldsProps) {
  const v = vcard ?? { firstName: '' };
  const inputClass =
    'w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent transition-colors duration-150';
  const labelClass = 'block text-xs font-medium text-text-muted';

  const inherit = v.inheritSocials === true;
  const socials = v.socials ?? [];

  const updateSocial = (idx: number, patch: Partial<VCardSocial>) => {
    const next = socials.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    onChange({ socials: next });
  };
  const removeSocial = (idx: number) => {
    onChange({ socials: socials.filter((_, i) => i !== idx) });
  };
  const addSocial = () => {
    onChange({ socials: [...socials, { kind: 'x', handle: '' }] });
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-text-muted leading-snug">
        Visitors who tap this on your page get a <code>.vcf</code> file that adds you straight to their contacts.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <label htmlFor={`vc-first-${linkKey}`} className={labelClass}>
            First name <span className="text-destructive">*</span>
          </label>
          <input
            id={`vc-first-${linkKey}`}
            type="text"
            value={v.firstName}
            onChange={(e) => onChange({ firstName: e.target.value })}
            placeholder="Andres"
            className={inputClass}
            required
          />
        </div>
        <div className="space-y-1">
          <label htmlFor={`vc-last-${linkKey}`} className={labelClass}>Last name</label>
          <input
            id={`vc-last-${linkKey}`}
            type="text"
            value={v.lastName ?? ''}
            onChange={(e) => onChange({ lastName: e.target.value })}
            placeholder="Moreno"
            className={inputClass}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor={`vc-email-${linkKey}`} className={labelClass}>Email</label>
          <input
            id={`vc-email-${linkKey}`}
            type="email"
            value={v.email ?? ''}
            onChange={(e) => onChange({ email: e.target.value })}
            placeholder="hi@example.com"
            className={inputClass}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor={`vc-phone-${linkKey}`} className={labelClass}>Phone</label>
          <input
            id={`vc-phone-${linkKey}`}
            type="tel"
            value={v.phone ?? ''}
            onChange={(e) => onChange({ phone: e.target.value })}
            placeholder="+1 555 123 4567"
            className={inputClass}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor={`vc-org-${linkKey}`} className={labelClass}>Organization</label>
          <input
            id={`vc-org-${linkKey}`}
            type="text"
            value={v.organization ?? ''}
            onChange={(e) => onChange({ organization: e.target.value })}
            placeholder="And More"
            className={inputClass}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor={`vc-title-${linkKey}`} className={labelClass}>Title</label>
          <input
            id={`vc-title-${linkKey}`}
            type="text"
            value={v.title ?? ''}
            onChange={(e) => onChange({ title: e.target.value })}
            placeholder="Founder"
            className={inputClass}
          />
        </div>
        <div className="space-y-1 sm:col-span-2">
          <label htmlFor={`vc-website-${linkKey}`} className={labelClass}>Website</label>
          <input
            id={`vc-website-${linkKey}`}
            type="url"
            value={v.website ?? ''}
            onChange={(e) => onChange({ website: e.target.value })}
            placeholder="https://andmore.dev"
            className={inputClass}
          />
        </div>
        <div className="space-y-1 sm:col-span-2">
          <label htmlFor={`vc-notes-${linkKey}`} className={labelClass}>Notes</label>
          <textarea
            id={`vc-notes-${linkKey}`}
            rows={2}
            value={v.notes ?? ''}
            onChange={(e) => onChange({ notes: e.target.value })}
            placeholder="A short reminder of where we met."
            className={`${inputClass} resize-y`}
          />
        </div>
      </div>

      <div className="pt-2 space-y-3 border-t border-border">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-foreground">Socials</p>
            <p className="text-xs text-text-muted leading-snug">
              Embedded as profile entries on the saved contact card.
            </p>
          </div>
          <label className="flex items-center gap-2 text-xs text-text-muted cursor-pointer select-none shrink-0">
            <input
              type="checkbox"
              checked={inherit}
              onChange={(e) => onChange({ inheritSocials: e.target.checked })}
              className="w-3.5 h-3.5 rounded border-border bg-muted text-accent focus:ring-accent focus:ring-offset-0 cursor-pointer"
            />
            Use socials from this page
          </label>
        </div>

        {inherit ? (
          <p className="text-xs text-text-muted bg-muted/40 border border-border rounded-lg px-3 py-2 leading-snug">
            Pulling X, LinkedIn, GitHub, and YouTube entries from this page's links at scan time. Edit them above in the page's Links section.
          </p>
        ) : (
          <div className="space-y-2">
            {socials.length === 0 ? (
              <p className="text-xs text-text-muted">
                No socials yet. Add one below or toggle "Use socials from this page" to inherit them automatically.
              </p>
            ) : (
              socials.map((s, i) => {
                const cfg = VCARD_SOCIAL_OPTIONS.find((o) => o.value === s.kind) ?? VCARD_SOCIAL_OPTIONS[0];
                return (
                  <div key={i} className="flex items-end gap-2">
                    <div className="space-y-1 w-36 shrink-0">
                      <label htmlFor={`vc-soc-kind-${linkKey}-${i}`} className={labelClass}>Kind</label>
                      <select
                        id={`vc-soc-kind-${linkKey}-${i}`}
                        value={s.kind}
                        onChange={(e) => updateSocial(i, { kind: e.target.value as VCardSocialKind })}
                        className={inputClass}
                      >
                        {VCARD_SOCIAL_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-1 flex-1">
                      <label htmlFor={`vc-soc-handle-${linkKey}-${i}`} className={labelClass}>Handle</label>
                      <div className="relative flex items-center">
                        {cfg.showAt && (
                          <span className="absolute left-3 text-sm text-text-muted select-none pointer-events-none">
                            @
                          </span>
                        )}
                        <input
                          id={`vc-soc-handle-${linkKey}-${i}`}
                          type="text"
                          value={s.handle.replace(/^@/, '')}
                          onChange={(e) => updateSocial(i, { handle: e.target.value })}
                          placeholder={cfg.placeholder}
                          className={`${inputClass} ${cfg.showAt ? 'pl-7' : ''}`}
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeSocial(i)}
                      className="p-2 rounded-lg text-text-muted hover:text-destructive hover:bg-surface transition-colors cursor-pointer"
                      aria-label={`Remove social ${i + 1}`}
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                  </div>
                );
              })
            )}
            <button
              type="button"
              onClick={addSocial}
              className="w-full flex items-center justify-center gap-1.5 border border-dashed border-border rounded-lg px-3 py-2 text-sm text-text-muted hover:text-foreground hover:border-accent/50 hover:bg-muted/40 transition-colors duration-150 cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              Add social
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
